import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDebounce } from 'ahooks'
import { Alert, App, Button, Form, Radio, Select, Typography } from 'antd'
import { siteBatchSetOwnershipSchema, type SiteBatchSetOwnershipInput } from '@super-mega/server/contracts/site'
import { useTRPC } from '@/trpc/react.ts'

type Values = { operationMode: SiteBatchSetOwnershipInput['operationMode']; organizationId?: number }

export function SiteBatchOwnershipModal({
  ids,
  onClose,
  onSaved
}: {
  ids: number[]
  onClose: () => void
  onSaved: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [form] = Form.useForm<Values>()
  const mode = Form.useWatch('operationMode', form)
  const [keyword, setKeyword] = useState('')
  const searchKeyword = useDebounce(keyword, { wait: 300 })
  const organizations = useQuery(
    trpc.lookup.organizations.queryOptions({ purpose: 'site.read', keyword: searchKeyword })
  )
  const mutation = useMutation(trpc.site.batchSetOwnership.mutationOptions())
  return (
    <ModalForm<Values>
      name="site-batch-ownership"
      open
      form={form}
      layout="vertical"
      width="30rem"
      title="批量修改场地归属"
      modalProps={{
        destroyOnHidden: true,
        mask: { closable: false },
        onCancel: onClose,
        keyboard: !mutation.isPending,
        closable: !mutation.isPending
      }}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      submitter={{
        searchConfig: { submitText: '确认修改', resetText: '取消' },
        resetButtonProps: { disabled: mutation.isPending },
        submitButtonProps: { loading: mutation.isPending }
      }}
      onFinish={async (values) => {
        try {
          const input = siteBatchSetOwnershipSchema.parse({
            ...values,
            ids,
            organizationId: values.operationMode === 'agent' ? null : values.organizationId
          })
          const result = await mutation.mutateAsync(input)
          void message.success(`已修改 ${result.count} 个场地`)
          await Promise.all([
            queryClient.invalidateQueries(trpc.site.list.queryFilter()),
            queryClient.invalidateQueries(trpc.site.options.queryFilter()),
            queryClient.invalidateQueries(trpc.room.list.queryFilter())
          ])
          onSaved()
          return true
        } catch (error) {
          void message.error(error instanceof Error ? error.message : '批量修改失败')
          return false
        }
      }}
    >
      <Typography.Paragraph type="secondary" className="mb-6!">
        已选择 <Typography.Text strong>{ids.length}</Typography.Text> 个场地，将统一应用以下设置。
      </Typography.Paragraph>
      <Form.Item label="经营类型" name="operationMode" rules={[{ required: true, message: '请选择经营类型' }]}>
        <Radio.Group
          optionType="button"
          buttonStyle="outline"
          disabled={mutation.isPending}
          onChange={() => form.setFieldValue('organizationId', undefined)}
          options={[
            { label: '自营', value: 'selfOperated', className: 'min-w-20 text-center' },
            { label: '代理', value: 'agent', className: 'min-w-20 text-center' }
          ]}
        />
      </Form.Item>
      <Form.Item
        label="归属主体"
        extra={mode === 'agent' ? '保存后将清空所选场地的归属主体。' : undefined}
        name="organizationId"
        rules={mode === 'selfOperated' ? [{ required: true, message: '自营场地必须选择归属主体' }] : []}
      >
        <Select
          disabled={mode !== 'selfOperated' || mutation.isPending}
          loading={organizations.isFetching}
          placeholder={mode === 'agent' ? '代理场地不绑定主体' : '搜索并选择经营主体'}
          showSearch={{ filterOption: false, onSearch: setKeyword }}
          options={(organizations.data?.data ?? []).map((item) => ({ label: item.name, value: item.id }))}
        />
      </Form.Item>
      <Typography.Paragraph type="secondary" className="mb-2! text-xs!">
        仅修改场地当前归属，不影响历史订单。
      </Typography.Paragraph>
      {organizations.isError ? (
        <Alert
          type="error"
          title="主体加载失败"
          action={
            <Button type="link" onClick={() => void organizations.refetch()}>
              重试
            </Button>
          }
        />
      ) : null}
    </ModalForm>
  )
}
